// Carrega a linha de `profiles` do usuário logado para o mini-onboarding
// (e para a tela de Perfil) lidar com o caso descrito na spec S01: o
// trigger do Postgres cria a linha no signup, mas ela só existe localmente
// depois de um pull (src/sync). Na prática o onboarding roda segundos
// depois do cadastro — o pull de baixo quase sempre resolve. `ensureProfileRow`
// (src/db/writes.ts) é só o fallback de última instância caso o pull também
// não traga nada (ex.: rede caiu bem entre o signUp e o onboarding).
import { eq } from 'drizzle-orm';
import { profiles, type Profile } from '../../db/schema';
import { ensureProfileRow } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';

/**
 * `pullOnce` é injetado (em vez de chamar `SyncServiceImpl` direto) para
 * manter esta função testável sem simular rede/Supabase — quem chama passa
 * `() => syncService.pullAll(db)` no app real.
 */
export async function loadOrCreateProfile(
  db: AppDatabase,
  userId: string,
  pullOnce: () => Promise<void>
): Promise<Profile> {
  const local = await db.select().from(profiles).where(eq(profiles.id, userId)).get();
  if (local) {
    return local;
  }

  try {
    await pullOnce();
  } catch (error) {
    // Sem rede ou pull falhou: segue para o fallback local abaixo em vez de
    // travar o onboarding.
    console.error('loadOrCreateProfile: pull falhou, tentando fallback local', error);
  }

  const afterPull = await db.select().from(profiles).where(eq(profiles.id, userId)).get();
  if (afterPull) {
    return afterPull;
  }

  return ensureProfileRow(db, userId);
}
